import { useMemo, useState } from 'react';
import {
  useAddMemberMutation,
  useGetProjectConfigsQuery,
  useListMembersQuery,
  useListUsersQuery,
  useRemoveMemberMutation,
  useSetMemberStagesMutation
} from '@/services/api';
import { useProjectScope } from '@/context/ProjectContext';
import { ErrorState } from '@/components/ErrorState';
import { parseApiError } from '@/utils/apiError';
import { useToast } from '@/components/toast/ToastProvider';
import { formatTimestamp } from '@/utils/format';
import type { ProjectMemberDto, UserRole } from '@/types/api';

const STAGE_OPTIONS: ReadonlyArray<{ id: string; label: string; agent_role: string }> = [
  { id: 'explore', label: 'Explore', agent_role: 'explorer' },
  { id: 'plan', label: 'Plan', agent_role: 'architect' },
  { id: 'code', label: 'Code', agent_role: 'coder' },
  { id: 'review', label: 'Review', agent_role: 'reviewer' },
  { id: 'test', label: 'Test', agent_role: 'tester' }
];

function useGateOffStages(projectId: string): Set<string> {
  const { data } = useGetProjectConfigsQuery(projectId);
  return useMemo(() => {
    const off = new Set<string>();
    const rows = data ?? [];
    for (const opt of STAGE_OPTIONS) {
      const row = rows.find((r) => r.key === `agent.${opt.agent_role}.pause_after`);
      if (row && row.value === false) off.add(opt.id);
    }
    return off;
  }, [data]);
}

function StageToggleList({
  selected,
  onToggle,
  gateOff
}: {
  selected: string[];
  onToggle: (stageId: string, on: boolean) => void;
  gateOff: Set<string>;
}): JSX.Element {
  return (
    <div className="field">
      <div className="stage-assign-list">
        {STAGE_OPTIONS.map((opt) => {
          const on = selected.includes(opt.id);
          const gateIsOff = gateOff.has(opt.id);
          const locked = gateIsOff && !on;
          return (
            <div key={opt.id} className="stage-assign-row">
              <span className="stage-assign-name">{opt.label}</span>
              {gateIsOff ? (
                <span className="stage-gate-off" title="This stage's pause gate is off for this project">
                  ⚠ gate off
                </span>
              ) : null}
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={opt.label}
                disabled={locked}
                title={
                  locked
                    ? 'This stage’s approval gate is off, so it never pauses for approval. Turn the gate on in Configuration to assign an approver.'
                    : undefined
                }
                className={`stage-switch ${on ? 'on' : ''}`}
                style={locked ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                onClick={() => {
                  if (locked) return;
                  onToggle(opt.id, !on);
                }}
              >
                <span className="stage-switch-knob" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="stage-assign-count">{selected.length} of {STAGE_OPTIONS.length} selected</div>
    </div>
  );
}

function StageChips({ stages }: { stages: string[] }): JSX.Element {
  if (stages.length === 0) {
    return <span className="stage-none">No stage assigned</span>;
  }
  const labels = STAGE_OPTIONS.filter((o) => stages.includes(o.id)).map((o) => o.label);
  if (labels.length > 3) {
    return (
      <span className="stage-count-badge" title={labels.join(', ')}>
        {labels.length}/{STAGE_OPTIONS.length} stages
      </span>
    );
  }
  return (
    <span className="stage-chips">
      {labels.map((l) => (
        <span key={l} className="stage-chip">{l}</span>
      ))}
    </span>
  );
}


export function MembersTab({ projectId, canWrite }: { projectId: string; canWrite: boolean }): JSX.Element {
  const { user } = useProjectScope();
  const membersQ = useListMembersQuery(projectId);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [dialog, setDialog] = useState<
    | { kind: 'add' }
    | { kind: 'remove'; member: ProjectMemberDto }
    | { kind: 'stages'; member: ProjectMemberDto }
    | { kind: 'last-admin' }
    | null
  >(null);

  const members = membersQ.data?.members ?? [];
  const adminCount = members.filter((m) => m.project_role === 'admin').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== 'all' && m.project_role !== roleFilter) return false;
      if (!q) return true;
      return (
        (m.name ?? '').toLowerCase().includes(q) || (m.email ?? '').toLowerCase().includes(q)
      );
    });
  }, [members, search, roleFilter]);

  if (membersQ.isLoading) {
    return (
      <div className="card">
        <div className="card-head">
          <div className="card-title">Project members</div>
        </div>
        <div className="members-toolbar" />
        <div className="overflow-x-auto">
          <table className="members-table" aria-busy="true" aria-label="Loading members">
            <thead>
              <tr>
                <th>Member</th>
                <th className="member-email-col">Email</th>
                <th style={{ width: 130 }}>Role</th>
                <th style={{ width: 190 }}>Stages</th>
                <th style={{ width: 120 }}>Added</th>
                <th style={{ width: 88, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }, (_, j) => (
                    <td key={j}>
                      <div
                        className="animate-skel"
                        style={{
                          height: 12,
                          borderRadius: 3,
                          background: 'var(--surface-sunken)',
                          width: `${45 + ((i + j) % 4) * 12}%`
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (membersQ.isError) {
    return (
      <ErrorState title="Couldn't load members" body="The project service is unreachable." onRetry={() => void membersQ.refetch()} />
    );
  }

  function onRemoveClick(member: ProjectMemberDto): void {
    if (member.project_role === 'admin' && adminCount <= 1) {
      setDialog({ kind: 'last-admin' });
    } else {
      setDialog({ kind: 'remove', member });
    }
  }

  return (
    <>
      <div className="card">
        <div className="card-head">
          <div className="card-title">Project members · {members.length}</div>
          {canWrite && (
            <button className="btn primary sm" onClick={() => setDialog({ kind: 'add' })}>
              + Add member
            </button>
          )}
        </div>
        <div className="members-toolbar">
          <input
            className="search-input"
            autoComplete="off-member-search"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search members"
          />
          <select
            className="toolbar-select"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value as 'all' | UserRole)}
            aria-label="Filter by role"
          >
            <option value="all">All roles</option>
            <option value="admin">admin</option>
            <option value="approver">approver</option>
            <option value="task_submitter">task_submitter</option>
          </select>
        </div>
        <div className="overflow-x-auto">
        <table className="members-table">
          <thead>
            <tr>
              <th>Member</th>
              <th className="member-email-col">Email</th>
              <th style={{ width: 130 }}>Role</th>
              <th style={{ width: 190 }}>Stages</th>
              <th style={{ width: 120 }}>Added</th>
              <th style={{ width: 88, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => {
              const isSelf = m.user_id === user.user_id;
              return (
                <tr key={m.user_id}>
                  <td className="member-col">
                    <div className="member-cell">
                      <span className={`member-avatar ${isSelf ? '' : 'alt'}`}>
                        {initials(m.name ?? m.email ?? '?')}
                      </span>
                      <div>
                        <div className="member-name" title={m.name ?? m.email ?? m.user_id}>
                          {m.name ?? m.email ?? m.user_id}
                        </div>
                        {isSelf ? <div className="member-you">you</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="member-email-col">
                    <span className="member-email" title={m.email ?? undefined}>
                      {m.email ?? '—'}
                    </span>
                  </td>
                  <td>
                    <span className={`role-chip ${m.project_role === 'admin' ? 'admin' : ''}`}>
                      {m.project_role}
                    </span>
                  </td>
                  <td>
                    {m.project_role === 'approver' ? (
                      <StageChips stages={m.stages ?? []} />
                    ) : (
                      <span className="stage-none">—</span>
                    )}
                  </td>
                  <td>
                    <span className="ts">{formatTimestamp(m.added_at).slice(0, 10)}</span>
                  </td>
                  <td>
                    <div className="row-actions">
                      {m.project_role === 'approver' && canWrite ? (
                        <button
                          className="icon-btn"
                          onClick={() => setDialog({ kind: 'stages', member: m })}
                          aria-label={`Assign stages for ${m.name ?? m.email ?? m.user_id}`}
                        >
                          ✎
                        </button>
                      ) : null}
                      <button
                        className="icon-btn"
                        disabled={!canWrite || isSelf}
                        onClick={() => onRemoveClick(m)}
                        aria-label={`Remove ${m.name ?? m.email ?? m.user_id}`}
                      >
                        ×
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      {dialog?.kind === 'add' && <AddMemberDialog projectId={projectId} onClose={() => setDialog(null)} />}
      {dialog?.kind === 'remove' && (
        <RemoveMemberDialog projectId={projectId} member={dialog.member} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'stages' && (
        <StageAssignmentDialog projectId={projectId} member={dialog.member} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'last-admin' && <LastAdminDialog onClose={() => setDialog(null)} />}
    </>
  );
}

function AddMemberDialog({ projectId, onClose }: { projectId: string; onClose: () => void }): JSX.Element {
  const [addMember, { isLoading }] = useAddMemberMutation();
  const usersQ = useListUsersQuery();
  const membersQ = useListMembersQuery(projectId);
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<{ user_id: string; email: string; role: UserRole } | null>(null);
  const [stages, setStages] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const gateOff = useGateOffStages(projectId);

  const memberIds = useMemo(
    () => new Set((membersQ.data?.members ?? []).map((m) => m.user_id)),
    [membersQ.data]
  );

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (usersQ.data?.users ?? []).filter((u) => {
      if (u.status !== 'active') return false;
      if (memberIds.has(u.user_id)) return false;
      if (!q) return true;
      return u.email.toLowerCase().includes(q);
    });
  }, [usersQ.data, memberIds, search]);

  const selectedIsApprover = selected?.role === 'approver';

  async function submit(): Promise<void> {
    if (!selected) return;
    setError(null);
    try {
      const ordered = STAGE_OPTIONS.filter((o) => stages.includes(o.id)).map((o) => o.id);
      await addMember({
        projectId,
        body: { user_id: selected.user_id, ...(selectedIsApprover ? { stages: ordered } : {}) }
      }).unwrap();
      toast.success(`${selected.email} added to the project.`);
      onClose();
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <Dialog title="Add member" sub="Add an existing AI Software Factory user to this project." onClose={onClose}>
      <div className="field">
        <label className="field-label" htmlFor="member-search">
          User
        </label>
        <input
          id="member-search"
          className="input"
          autoComplete="off"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setSelected(null);
          }}
        />
      </div>
      <div className="user-search-list" role="listbox" aria-label="Users">
        {usersQ.isLoading ? (
          <div className="user-search-empty">Loading users…</div>
        ) : candidates.length === 0 ? (
          <div className="user-search-empty">No matching users.</div>
        ) : (
          candidates.map((u) => {
            const isSel = selected?.user_id === u.user_id;
            return (
              <div
                key={u.user_id}
                className={`user-search-row ${isSel ? 'selected' : ''}`}
                role="option"
                aria-selected={isSel}
                tabIndex={0}
                onClick={() => setSelected({ user_id: u.user_id, email: u.email, role: u.system_role })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelected({ user_id: u.user_id, email: u.email, role: u.system_role });
                  }
                }}
              >
                <span className="member-avatar alt">{initials(u.display_name ?? u.email)}</span>
                <div className="user-search-meta">
                  <div className="member-name">{u.display_name ?? u.email}</div>
                  <div className="member-email">{u.email}</div>
                </div>
                <span className={`role-chip ${u.system_role === 'admin' ? 'admin' : ''}`}>
                  {u.system_role}
                </span>
              </div>
            );
          })
        )}
      </div>
      {selectedIsApprover ? (
        <StageToggleList
          selected={stages}
          onToggle={(id, on) => setStages((prev) => (on ? [...prev, id] : prev.filter((s) => s !== id)))}
          gateOff={gateOff}
        />
      ) : null}
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <DialogFoot>
        <button className="btn ghost sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary sm" disabled={!selected || isLoading} onClick={() => void submit()}>
          Add member
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function StageAssignmentDialog({
  projectId,
  member,
  onClose
}: {
  projectId: string;
  member: ProjectMemberDto;
  onClose: () => void;
}): JSX.Element {
  const [setStages, { isLoading }] = useSetMemberStagesMutation();
  const toast = useToast();
  const gateOff = useGateOffStages(projectId);
  const [selected, setSelected] = useState<string[]>(member.stages ?? []);
  const [error, setError] = useState<string | null>(null);

  function toggle(stageId: string, on: boolean): void {
    setSelected((prev) => (on ? [...prev, stageId] : prev.filter((s) => s !== stageId)));
  }

  async function submit(): Promise<void> {
    setError(null);
    try {
      const ordered = STAGE_OPTIONS.filter((o) => selected.includes(o.id)).map((o) => o.id);
      await setStages({ projectId, userId: member.user_id, body: { stages: ordered } }).unwrap();
      toast.success(`Stages updated for ${member.name ?? member.email ?? member.user_id}.`);
      onClose();
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }

  return (
    <Dialog
      title={`Assign stages · ${member.name ?? member.email ?? member.user_id}`}
      sub="Stages this approver can decide and is accountable for."
      onClose={onClose}
    >
      <StageToggleList selected={selected} onToggle={toggle} gateOff={gateOff} />
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <DialogFoot>
        <button className="btn ghost sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary sm" disabled={isLoading} onClick={() => void submit()}>
          Save
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function RemoveMemberDialog({
  projectId,
  member,
  onClose
}: {
  projectId: string;
  member: ProjectMemberDto;
  onClose: () => void;
}): JSX.Element {
  const [removeMember, { isLoading }] = useRemoveMemberMutation();
  const toast = useToast();
  const [error, setError] = useState<string | null>(null);
  async function submit(): Promise<void> {
    setError(null);
    try {
      await removeMember({ projectId, userId: member.user_id }).unwrap();
      toast.success(`${member.name} removed from the project.`);
      onClose();
    } catch (err) {
      setError(parseApiError(err).message);
    }
  }
  return (
    <Dialog
      title={`Remove ${member.name} from this project?`}
      sub="They will lose access immediately. Their past activity stays attributed to them in the audit log."
      onClose={onClose}
    >
      <MemberPreview member={member} detail={`${member.email ?? member.user_id} · ${member.project_role}`} />
      {error && (
        <div className="field-err" role="alert">
          {error}
        </div>
      )}
      <DialogFoot>
        <button className="btn ghost sm" onClick={onClose}>
          Cancel
        </button>
        <button className="btn danger sm" disabled={isLoading} onClick={() => void submit()}>
          Remove member
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function LastAdminDialog({ onClose }: { onClose: () => void }): JSX.Element {
  return (
    <Dialog title="Can't remove the only admin" sub="A project must have at least one admin." onClose={onClose}>
      <div className="guard">Add another admin first, then come back to remove this one.</div>
      <DialogFoot>
        <button className="btn primary sm" onClick={onClose}>
          Got it
        </button>
      </DialogFoot>
    </Dialog>
  );
}

function Dialog({
  title,
  sub,
  onClose,
  children
}: {
  title: string;
  sub: string;
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="overlay open" role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-head">
          <h2 className="dialog-title">{title}</h2>
          <p className="dialog-sub">{sub}</p>
        </div>
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  );
}

function DialogFoot({ children }: { children: React.ReactNode }): JSX.Element {
  return <div className="dialog-foot">{children}</div>;
}

function MemberPreview({ member, detail }: { member: ProjectMemberDto; detail: string }): JSX.Element {
  return (
    <div
      className="member-cell"
      style={{
        padding: '10px 14px',
        background: 'var(--surface-alt)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 16
      }}
    >
      <span className="member-avatar alt">{initials(member.name ?? member.email ?? '?')}</span>
      <div>
        <div className="member-name">{member.name ?? member.email ?? member.user_id}</div>
        <div className="member-email">{detail}</div>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}
